import { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ExternalLink, Search } from 'lucide-react';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { SuggestionThumbnail } from '@/components/template-ideas/SuggestionThumbnail';
import { formatDateTime } from '@/lib/format';
import type { AdminTemplateSuggestion, SuggestionStatus } from '@/lib/template-suggestions/types';
import {
  ADMIN_REVIEW_TEMPLATE_SUGGESTION, ADMIN_TEMPLATE_SUGGESTIONS,
} from '@/queries/templateSuggestions';

const PAGE_SIZE = 25;
const STATUSES: SuggestionStatus[] = ['pending', 'public', 'promoted', 'rejected'];

type PendingAction =
  | { kind: 'reject'; row: AdminTemplateSuggestion }
  | { kind: 'promote'; row: AdminTemplateSuggestion };

export default function AdminTemplateSuggestions() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [action, setAction] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState('');
  const [brandKitId, setBrandKitId] = useState('');

  const { data, loading, error, refetch } = useQuery(ADMIN_TEMPLATE_SUGGESTIONS, {
    variables: {
      status: status === 'all' ? null : status,
      search: search.trim() || null,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
  });
  const [review, reviewState] = useMutation(ADMIN_REVIEW_TEMPLATE_SUGGESTION);

  const rows: AdminTemplateSuggestion[] = data?.adminTemplateSuggestions?.data ?? [];
  const pageCount: number = data?.adminTemplateSuggestions?.pageCount ?? 1;

  const submitReview = useCallback(
    async (vars: { id: string; status: SuggestionStatus; reason?: string; brandKitId?: string }) => {
      try {
        await review({
          variables: {
            id: vars.id,
            status: vars.status,
            reason: vars.reason || null,
            brandKitId: vars.brandKitId || null,
          },
        });
        toast({ description: t('admin.templateSuggestions.saved') });
        setAction(null);
        setReason('');
        setBrandKitId('');
        await refetch();
      } catch {
        toast({ variant: 'destructive', description: t('admin.templateSuggestions.error') });
      }
    },
    [refetch, review, t, toast],
  );

  return (
    <Layout>
      <div className="mx-auto w-full max-w-6xl space-y-4 px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.templateSuggestions.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-[240px] flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  value={search}
                  placeholder={t('admin.templateSuggestions.searchPlaceholder')}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                />
              </div>
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.templateSuggestions.filterAll')}</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{t(`templateIdeas.status.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.templateSuggestions.columns.suggestion')}</TableHead>
                  <TableHead>{t('admin.templateSuggestions.columns.author')}</TableHead>
                  <TableHead>{t('admin.templateSuggestions.columns.votes')}</TableHead>
                  <TableHead>{t('admin.templateSuggestions.columns.status')}</TableHead>
                  <TableHead>{t('admin.templateSuggestions.columns.createdAt')}</TableHead>
                  <TableHead className="text-right">{t('admin.templateSuggestions.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* A failed query used to render the empty state, which an
                    admin reads as "nothing to moderate" — including when this
                    frontend meets a backend without the new schema. */}
                {error && (
                  <TableRow>
                    <TableCell colSpan={6} className="space-y-2 text-center">
                      <p className="text-destructive">{t('admin.templateSuggestions.loadError')}</p>
                      <Button size="sm" variant="secondary" onClick={() => { refetch(); }}>
                        {t('admin.templateSuggestions.retry')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
                {!error && !loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      {t('admin.templateSuggestions.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="max-w-[360px]">
                      <div className="flex gap-3">
                        {/* The preview is the point of this column: moderating a
                            queue by opening every link in a new tab is the thing
                            it exists to avoid. */}
                        <a
                          href={row.submitted_url}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={row.title}
                        >
                          <SuggestionThumbnail url={row.thumbnail_url} className="h-16 w-11" />
                        </a>
                        <div className="min-w-0">
                          <p className="truncate font-medium">{row.title}</p>
                          <p className="truncate text-xs text-muted-foreground">{row.use_case}</p>
                          <a
                            href={row.submitted_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {row.platform}
                          </a>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{row.user_name}</p>
                      <p className="text-xs text-muted-foreground">{row.user_email}</p>
                    </TableCell>
                    <TableCell>{row.vote_count}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{t(`templateIdeas.status.${row.status}`)}</Badge>
                      {row.review_reason && (
                        <p className="max-w-[180px] truncate text-xs text-muted-foreground">{row.review_reason}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell className="space-x-1 text-right">
                      {row.status === 'pending' && (
                        <Button size="sm" variant="secondary" disabled={reviewState.loading}
                          onClick={() => submitReview({ id: row.id, status: 'public' })}>
                          {t('admin.templateSuggestions.approve')}
                        </Button>
                      )}
                      {row.status === 'public' && (
                        <Button size="sm" disabled={reviewState.loading}
                          onClick={() => { setBrandKitId(''); setAction({ kind: 'promote', row }); }}>
                          {t('admin.templateSuggestions.promote')}
                        </Button>
                      )}
                      {row.status !== 'rejected' && (
                        <Button size="sm" variant="destructive" disabled={reviewState.loading}
                          onClick={() => { setReason(''); setAction({ kind: 'reject', row }); }}>
                          {t('admin.templateSuggestions.reject')}
                        </Button>
                      )}
                      {row.status === 'rejected' && (
                        <Button size="sm" variant="ghost" disabled={reviewState.loading}
                          onClick={() => submitReview({ id: row.id, status: 'pending' })}>
                          {t('admin.templateSuggestions.reopen')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center justify-end gap-2">
              <Button size="icon" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">{page + 1} / {Math.max(pageCount, 1)}</span>
              <Button size="icon" variant="ghost" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={action?.kind === 'reject'} onOpenChange={(open) => !open && setAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.templateSuggestions.rejectTitle')}</DialogTitle>
            <DialogDescription>{t('admin.templateSuggestions.rejectDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reject-reason">{t('admin.templateSuggestions.reasonLabel')}</Label>
            <Input id="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAction(null)}>
              {t('admin.templateSuggestions.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={!reason.trim() || reviewState.loading}
              onClick={() => action && submitReview({ id: action.row.id, status: 'rejected', reason: reason.trim() })}
            >
              {t('admin.templateSuggestions.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={action?.kind === 'promote'} onOpenChange={(open) => !open && setAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.templateSuggestions.promoteTitle')}</DialogTitle>
            <DialogDescription>{t('admin.templateSuggestions.promoteDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="promote-kit">{t('admin.templateSuggestions.brandKitLabel')}</Label>
            <Input id="promote-kit" value={brandKitId} onChange={(e) => setBrandKitId(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAction(null)}>
              {t('admin.templateSuggestions.cancel')}
            </Button>
            <Button
              disabled={reviewState.loading}
              onClick={() => action && submitReview({
                id: action.row.id, status: 'promoted', brandKitId: brandKitId.trim() || undefined,
              })}
            >
              {t('admin.templateSuggestions.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
